"use client";

import { type ReactNode, useEffect, useRef } from "react";

import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";

/**
 * How fast the space bg moves vs page scroll.
 * 1 = glued to content; 0 = fully still; 0.25 = clearly slower lag.
 */
export const PARALLAX_SPACE_SCROLL_FACTOR = 0.25;

/** Default tiled space texture under the purple grade. */
export const PARALLAX_SPACE_BG_DEFAULT_SRC =
  "https://assets.playmath.org/shared/platform/backgrounds/space.webp";

/** CSS class for the purple space gradient overlay (opacity in globals.css). */
export const PARALLAX_SPACE_BG_GRADIENT_CLASS = "parallax-space-bg-gradient";

type ParallaxSpaceBgProps = {
  baseSrc?: string;
  className?: string;
  /**
   * Fraction of scroll distance the background travels (0–1).
   * Lower = slower. Replaces the old Rellax speedMultiplier, which only
   * shifted the texture while the layer itself still scrolled 1:1.
   */
  scrollFactor?: number;
  /** Optional sky layers (e.g. PiShootingStars) painted above the purple grade. */
  children?: ReactNode;
};

/**
 * Fixed viewport space backdrop: scrolling space texture with a purple
 * gradient grade on top (normal blend at 50% opacity).
 */
export function ParallaxSpaceBg({
  baseSrc = PARALLAX_SPACE_BG_DEFAULT_SRC,
  className,
  scrollFactor = PARALLAX_SPACE_SCROLL_FACTOR,
  children,
}: ParallaxSpaceBgProps) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const factor = Math.min(1, Math.max(0, scrollFactor));
  const overlayFrozen = useTheaterOverlayFrozen();
  const overlayFrozenRef = useRef(overlayFrozen);
  overlayFrozenRef.current = overlayFrozen;

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    let raf = 0;

    const sizeForTravel = () => {
      const scrollable = Math.max(
        0,
        (document.documentElement?.scrollHeight || 0) - window.innerHeight
      );
      // Tall enough that slow upward travel never exposes a gap.
      const travelPx = scrollable * factor;
      el.style.height = `calc(100% + ${Math.ceil(travelPx)}px)`;
    };

    const apply = () => {
      const y = window.scrollY * factor;
      el.style.transform = `translate3d(0, ${-y}px, 0)`;
    };

    // Scroll only needs a transform; scrollHeight reads on every scroll force layout.
    const onScroll = () => {
      if (overlayFrozenRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };

    const onResizeOrLayout = () => {
      if (overlayFrozenRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        sizeForTravel();
        apply();
      });
    };

    sizeForTravel();
    apply();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResizeOrLayout, { passive: true });

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => onResizeOrLayout()) : null;
    try {
      ro?.observe(document.documentElement);
    } catch (err) {
      console.error("ResizeObserver failed to observe document.documentElement:", err);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResizeOrLayout);
      ro?.disconnect();
    };
  }, [factor]);

  return (
    <div
      aria-hidden
      data-testid="parallax-space-bg"
      className={className ?? ""}
      style={{
        // Fixed to the viewport so “slower” actually means lag vs content —
        // not a texture nudge inside a layer that still scrolls 1:1.
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
        // Solid fallback under the texture/grade (mobile safe-area gutters, load gaps).
        backgroundColor: "#000",
        // Stacking context so sky children (e.g. shooting stars) sit above the grade.
        isolation: "isolate",
      }}
    >
      <div
        ref={innerRef}
        data-testid="parallax-space-bg-inner"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          backgroundImage: `url(${baseSrc})`,
          backgroundRepeat: "repeat-y",
          backgroundSize: "100% auto",
          backgroundPosition: "50% 0px",
          willChange: "transform",
          backfaceVisibility: "hidden",
          transform: "translate3d(0, 0, 0)",
          userSelect: "none",
        }}
      />
      {/* Viewport-fixed purple grade so the tint stays consistent while the texture scrolls. */}
      <div
        data-testid="parallax-space-bg-overlay"
        className={PARALLAX_SPACE_BG_GRADIENT_CLASS}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
        }}
      />
      {children}
    </div>
  );
}

export default ParallaxSpaceBg;
